import React, { useRef, useCallback } from 'react';
import classnames from 'classnames';
import Wahanda from 'common/wahanda';
import App from 'common/backbone-app';
import { SideBarAnalytics } from 'common/analytics';
import style from './SidebarInfo.scss';
import { ChatSourceType } from 'src/tracking-container/events/chat';

declare global {
  interface Window {
    productFruits?: {
      api?: {
        announcementsV2?: {
          attachNewsWidgetToElement: (element: HTMLElement) => void;
          listen: (event: string, callback: (data: { count: number }) => void) => void;
        };
      };
    };
  }
}

const LANG = Wahanda.lang.home.contacts;
const POLL_INTERVAL_MS = 500;
const MAX_POLL_ATTEMPTS = 60;

const getNewsfeedApi = () => window.productFruits?.api?.announcementsV2;

const useProductFruitsNewsfeed = () => {
  const ref = useRef<HTMLAnchorElement>(null);
  const [unreadCount, setUnreadCount] = React.useState(0);

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let cancelled = false;

    const attachWidget = () => {
      const api = getNewsfeedApi();
      if (!api) return false;

      api.listen('newsfeed-unread-count-changed', (data) => {
        if (!cancelled) setUnreadCount(data.count);
      });
      api.attachNewsWidgetToElement(element);
      return true;
    };

    if (attachWidget()) {
      return () => {
        cancelled = true;
      };
    }

    let attempts = 0;
    const interval = setInterval(() => {
      if (cancelled || ++attempts >= MAX_POLL_ATTEMPTS) {
        clearInterval(interval);
        return;
      }
      if (attachWidget()) clearInterval(interval);
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return { ref, unreadCount };
};

interface WhatsNewNewsfeedProps {
  liveChatSource: ChatSourceType;
}

export const WhatsNewNewsfeed: React.FC<WhatsNewNewsfeedProps> = ({ liveChatSource }) => {
  const { ref, unreadCount } = useProductFruitsNewsfeed();

  const handleClick = useCallback(() => {
    SideBarAnalytics.trackWhatsNewClick({ property: liveChatSource });
  }, [liveChatSource]);

  if (!App.isProductFruitsInitialized) {
    return null;
  }

  return (
    <a
      ref={ref}
      data-test="sidebar-info-link-whats-new"
      className={classnames(style.link, style.whatIsNew)}
      role="button"
      onClick={handleClick}
    >
      {LANG.whatIsNew}
      {unreadCount > 0 && <span className={style.badge}>{unreadCount}</span>}
    </a>
  );
};
